import React, { useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { evaluationApi, Evaluation, Target } from '../../services/api';
import { AutoGrowTextarea } from '../ui/AutoGrowTextarea';
import { compactInputClass, compactTextareaClass } from '../ui/formClasses';

export function EditableTargets({ evaluationId, targets, reload, evaluations }: { evaluationId: string; targets: Target[]; reload: () => void; evaluations: Evaluation[] }) {
  const [copySource, setCopySource] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState('');
  const [importError, setImportError] = useState('');
  const save = async (item: Target, patch: Partial<Target>) => {
    await evaluationApi.updateTarget(evaluationId, item.id, patch);
    reload();
  };
  const importFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportMessage(`Uploading ${file.name}...`);
    setImportError('');
    try {
      const response = await evaluationApi.importTargets(evaluationId, file);
      await reload();
      setImportMessage(`Imported ${response.data.length} targets from ${file.name}.`);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setImportError(detail.map((item: any) => item.msg).join(', '));
      } else if (typeof detail === 'string') {
        setImportError(detail);
      } else {
        setImportError(err.message || 'Could not import targets.');
      }
      setImportMessage('');
    } finally {
      setImporting(false);
      input.value = '';
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <button onClick={async () => { await evaluationApi.createTarget(evaluationId, { vendor: '', product_name: 'New target', description: '', notes: '', sort_order: targets.length }); reload(); }} className="inline-flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg"><PlusIcon className="w-4 h-4 mr-1" /> Add target</button>
        <label className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${importing ? 'bg-gray-300 dark:bg-gray-700 text-gray-500' : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-pointer'}`}>
          {importing ? 'Importing...' : 'Import spreadsheet'}
          <input disabled={importing} type="file" accept=".csv,.xlsx,.xls" onChange={importFile} className="sr-only" />
        </label>
        <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white" value={copySource} onChange={(e) => setCopySource(e.target.value)}>
          <option value="">Copy targets from...</option>
          {evaluations.filter((e) => e.id !== evaluationId).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <button disabled={!copySource} onClick={async () => { await evaluationApi.copyTargets(evaluationId, copySource); setCopySource(''); reload(); }} className="px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg disabled:opacity-50">Copy</button>
      </div>
      {importMessage && <div className="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">{importMessage}</div>}
      {importError && <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{importError}</div>}
      <div className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        {targets.map((item) => (
          <div key={item.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 p-2.5 bg-white dark:bg-gray-800 items-start">
            <input className={`${compactInputClass} md:col-span-2`} defaultValue={item.vendor || ''} placeholder="Vendor" onBlur={(e) => save(item, { vendor: e.target.value })} />
            <input className={`${compactInputClass} md:col-span-3`} defaultValue={item.product_name} placeholder="Product" onBlur={(e) => save(item, { product_name: e.target.value })} />
            <AutoGrowTextarea className={`${compactTextareaClass} md:col-span-3`} defaultValue={item.description || ''} placeholder="Description" onBlur={(e) => save(item, { description: e.target.value })} rows={1} />
            <AutoGrowTextarea className={`${compactTextareaClass} md:col-span-3`} defaultValue={item.notes || ''} placeholder="Notes" onBlur={(e) => save(item, { notes: e.target.value })} rows={1} />
            <button onClick={async () => { await evaluationApi.deleteTarget(evaluationId, item.id); reload(); }} className="h-9 inline-flex items-center justify-center text-gray-400 hover:text-red-600 dark:hover:text-red-400"><TrashIcon className="w-5 h-5" /></button>
          </div>
        ))}
        {targets.length === 0 && <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">Add targets manually, import a spreadsheet, or copy from another evaluation.</div>}
      </div>
    </div>
  );
}
